import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as vendorService from '../services/vendorService';
import { Vendor, VendorFilters } from '../services/vendorService';
import { useAuth } from '../context/AuthContext';

interface VendorContextType {
  vendors: Vendor[];
  favorites: string[];
  favoriteVendors: Vendor[];
  isLoading: boolean;
  error: string | null;
  loadVendors: (filters?: VendorFilters, pagination?: { page?: number; limit?: number }) => Promise<void>;
  refreshVendors: () => Promise<void>;
  getVendorById: (id: string) => Promise<Vendor | null>;
  toggleFavorite: (vendorId: string) => Promise<void>;
  isFavorite: (vendorId: string) => boolean;
  loadFavorites: () => Promise<void>;
}

const VendorContext = createContext<VendorContextType | undefined>(undefined);

export const VendorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [favoriteVendors, setFavoriteVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();

  const loadVendors = useCallback(async (
    filters?: VendorFilters,
    pagination?: { page?: number; limit?: number }
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await vendorService.getVendors(filters, pagination);
      
      let vendorsArray: Vendor[] = [];
      
      if (Array.isArray(result)) {
        vendorsArray = result;
      } else if (result && typeof result === 'object' && 'vendors' in result) {
        vendorsArray = Array.isArray(result.vendors) ? result.vendors : [];
      }
      
      setVendors(vendorsArray);
    } catch (err: any) {
      console.error('[VendorContext] Error loading vendors:', err);
      setError(err.message || 'Failed to load vendors');
      setVendors([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshVendors = useCallback(async () => {
    await loadVendors();
  }, [loadVendors]);

  const getVendorById = useCallback(async (id: string): Promise<Vendor | null> => {
    try {
      const vendor = await vendorService.getVendorById(id);
      return vendor;
    } catch (err: any) {
      console.error('[VendorContext] Error fetching vendor:', err);
      return null;
    }
  }, []);

  const toggleFavorite = useCallback(async (vendorId: string) => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const isCurrentlyFavorite = favorites.includes(vendorId);
      
      if (isCurrentlyFavorite) {
        await vendorService.removeFavorite(vendorId);
        setFavorites(prev => prev.filter(id => id !== vendorId));
        setFavoriteVendors(prev => prev.filter(v => v.id !== vendorId));
      } else {
        await vendorService.addFavorite(vendorId);
        setFavorites(prev => [...prev, vendorId]);
        const vendor = vendors.find(v => v.id === vendorId);
        if (vendor) setFavoriteVendors(prev => [...prev, vendor]);
      }
    } catch (err: any) {
      console.error('[VendorContext] Error toggling favorite:', err);
      window.alert('Could not save your favorite. Please try again.');
    }
  }, [favorites, vendors, isAuthenticated]);

  const isFavorite = useCallback((vendorId: string): boolean => {
    return favorites.includes(vendorId);
  }, [favorites]);

  const loadFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const favoriteVendorsResult = await vendorService.getUserFavorites();
      setFavorites(favoriteVendorsResult.map(v => v.id));
      setFavoriteVendors(favoriteVendorsResult);
    } catch (err: any) {
      console.error('[VendorContext] Error loading favorites:', err);
    }
  }, [isAuthenticated]);

  // Favorites persisted on the backend were never loaded on mount/login —
  // isFavorite() always started from an empty list, so a vendor liked in a
  // previous session showed as unliked, and clicking it called addFavorite
  // again, which the backend correctly rejects (already favorited), so the
  // heart button appeared to do nothing.
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadFavorites();
    } else {
      setFavorites([]);
      setFavoriteVendors([]);
    }
  }, [isAuthenticated, user?.id, loadFavorites]);

  return (
    <VendorContext.Provider
      value={{
        vendors,
        favorites,
        favoriteVendors,
        isLoading,
        error,
        loadVendors,
        refreshVendors,
        getVendorById,
        toggleFavorite,
        isFavorite,
        loadFavorites,
      }}
    >
      {children}
    </VendorContext.Provider>
  );
};

export const useVendor = (): VendorContextType => {
  const context = useContext(VendorContext);
  if (context === undefined) {
    throw new Error('useVendor must be used within a VendorProvider');
  }
  return context;
};








